/**
 * Mapeo del shape interno del front (lo que recolectan los forms) al shape EXACTO
 * que espera el BFF .NET en /ap/contacto y /prepaga/contacto.
 *
 * CONTRATO BFF (fuente de verdad — NO inventar):
 * - Telefono: string plano (solo número) + CodigoArea: string separado.
 *   (el front recolecta telefono como objeto {prefijo/CodigoArea, numero/Numero})
 * - Profesion: int? (el front tiene el value como string "1".."N")
 * - Sumas: number (long / long?)
 * - Enums (CondicionIVA / TipoPersona / CondicionIIBB / TipoDocumento):
 *   el BFF deserializa con System.Text.Json + JsonStringEnumConverter
 *   (ver BffSegurarse.Api/Program.cs L38), o sea acepta el NOMBRE DEL MIEMBRO
 *   del enum C# como string (case-insensitive). NO acepta numeric-string ("2"),
 *   NO usa el [Description]. Por eso traducimos value/label del form -> nombre de miembro.
 */

// ---------- Enums: value del form -> nombre de miembro C# del BFF ----------

/**
 * CondicionIVA (BffSegurarse.Api/Models/Enums/CondicionIVA.cs).
 * El form trae el `codigo` del monolito (getcondicionivajson -> cond.codigo) como string.
 * Esos códigos coinciden con el valor byte del enum del BFF.
 */
const CONDICION_IVA_POR_CODIGO: Record<string, string> = {
  '1': 'ConsumidorFinal',
  '2': 'ResponsableGranContribuyente',
  '3': 'Exento',
  '4': 'Monotributista',
  '5': 'NoCategorizado',
  '6': 'ResponsableInscripto',
  '7': 'ResponsableNoInscripto',
  '8': 'NoResponsable',
  '9': 'ContribSinPercepcion',
  '10': 'NoGravado',
  '11': 'ServiciosAlExterior',
  '13': 'SujetoNoIdentificado',
};

/**
 * CondicionIngresosBrutos (BffSegurarse.Api/Models/Enums/CondicionIngresosBrutos.cs).
 * El form trae el value del array local (CondicionIIBB.ts: "2".."5") = byte del enum.
 */
const CONDICION_IIBB_POR_CODIGO: Record<string, string> = {
  '1': 'RegimenSimplificado',
  '2': 'CLocal',
  '3': 'NoInscripto',
  '4': 'Exento',
  '5': 'ConvenioMultilateral',
  '6': 'Codigo00',
  '7': 'Codigo01',
  '8': 'Codigo02',
  '9': 'Codigo03',
  '10': 'Codigo04',
  '11': 'Codigo05',
  '12': 'Codigo06',
  '13': 'Codigo07',
  '14': 'Codigo08',
  '15': 'Codigo09',
  '16': 'Codigo10',
  '17': 'Codigo11',
  '18': 'Codigo12',
  '19': 'Codigo13',
  '20': 'Codigo14',
  '21': 'Codigo15',
};

/**
 * TipoDocumento (BffSegurarse.Api/Models/Enums/TipoDocumento.cs).
 * El form usa value 'dni' | 'cuit' | 'cuil' (los selects del front mapean CUIL/CUIT a 'cuit').
 */
const TIPO_DOCUMENTO_POR_VALUE: Record<string, string> = {
  dni: 'DNI',
  le: 'LE',
  lc: 'LC',
  cuit: 'CUIT',
  cuil: 'CUIT', // el front no distingue CUIL/CUIT a nivel enum del monolito; ambos -> CUIT
  pasaporte: 'Pasaporte',
};

/**
 * TipoPersona (BffSegurarse.Api/Models/Enums/TipoPersona.cs).
 * El form (TipoPersona.tsx) ya usa value 'Fisica' | 'PersonaJuridica' = nombre del miembro C#.
 */
const TIPO_PERSONA_VALIDOS = new Set(['Fisica', 'PersonaJuridica']);

const norm = (v: unknown): string => (v ?? '').toString().trim();

export const mapCondicionIVA = (codigo: unknown): string => {
  const k = norm(codigo);
  return CONDICION_IVA_POR_CODIGO[k] ?? '';
};

export const mapCondicionIIBB = (codigo: unknown): string => {
  const k = norm(codigo);
  return CONDICION_IIBB_POR_CODIGO[k] ?? '';
};

export const mapTipoDocumento = (value: unknown): string => {
  const k = norm(value).toLowerCase();
  return TIPO_DOCUMENTO_POR_VALUE[k] ?? '';
};

export const mapTipoPersona = (value: unknown): string => {
  const k = norm(value);
  return TIPO_PERSONA_VALIDOS.has(k) ? k : '';
};

/** profesion: el form tiene el value como string ("1".."N"); el BFF espera int?. */
export const mapProfesion = (value: unknown): number | null => {
  const k = norm(value);
  if (k === '') return null;
  const n = Number(k);
  return Number.isFinite(n) ? n : null;
};

/**
 * Convierte a number entero (long que espera el BFF). Acepta string formateado o
 * number y REDONDEA: un float (ej. (10/100)*suma = 4000004.4000000004) rompía el
 * bind a long en el BFF con 400. 0 si no es válido.
 */
export const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : 0;
  const k = norm(value).replace(/[^\d.-]/g, '');
  const n = Number(k);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

// ============================================================================
// §12.2 — Shape tipado para POST {BFF}/empresas/lead (lead de empresas).
// El BFF NO coacciona tipos: phone.* y zipCode = number; documentNumber = string.
// Si un número viene vacío/NaN, OMITIMOS la clave (no NaN/null en subobjetos).
// ============================================================================

export type PersonType = 'PHYSICAL' | 'LEGAL';

export interface EmpresasLeadPhone {
  countryCode: number;
  areaCode: number;
  number: number;
}

export interface EmpresasLeadAddress {
  street?: string;
  number?: string;
  city?: string;
  province?: string;
  zipCode?: number;
  country: string;
}

export interface EmpresasLeadContact {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  phone?: EmpresasLeadPhone;
  documentType?: string;
  documentNumber?: string;
  address?: EmpresasLeadAddress;
}

export interface EmpresasLeadBody {
  rubro: string;
  personType?: PersonType;
  contact: EmpresasLeadContact;
  quote: Record<string, unknown>;
  source: 'Web';
  refererUrl?: string;
  landingUrl?: string;
}

/** Parsea a number entero estricto; undefined si vacío/NaN (no devuelve 0). */
const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const k = norm(value).replace(/[^\d.-]/g, '');
  if (k === '') return undefined;
  const n = Number(k);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Teléfono §12.2: { countryCode:54, areaCode, number } con tipos number.
 * undefined si prefijo o número quedan vacíos/NaN (no se manda phone con NaN).
 */
export const mapPhone = (
  prefijo: unknown,
  numero: unknown
): EmpresasLeadPhone | undefined => {
  const areaCode = toNumberOrUndefined(prefijo);
  const number = toNumberOrUndefined(numero);
  if (areaCode === undefined || number === undefined) return undefined;
  return { countryCode: 54, areaCode, number };
};

/** rubros.js tipoPersona ('FISICA'|'JURIDICA'|'AMBOS') del front. */
type TipoPersonaRubro = 'FISICA' | 'JURIDICA' | 'AMBOS' | string;

interface PersonTypeInput {
  /** value del tipo de documento del form ('dni'|'cuit'|'cuil'...). */
  tipoDoc?: unknown;
  /** rubros.js `tipoPersona` de la landing. */
  tipoPersonaRubro?: TipoPersonaRubro;
  /**
   * personType por defecto para rubros AMBOS, decidido por el FORM (no por el
   * rubro). FormularioIndividuo (persona física) pasa 'PHYSICAL' para que un
   * rubro AMBOS (Embarcacion, AP...) lleve el personType que crmbridge exige.
   * NO pisa el tipo del rubro: solo aplica cuando el rubro es AMBOS y no hay
   * tipoDoc que resuelva. Para rubros FISICA/JURIDICA el tipo del rubro manda.
   */
  defaultPersonTypeForBoth?: PersonType;
}

/**
 * Deriva personType §12.2. Prioridad (el TIPO DEL RUBRO es decisivo, no se pisa
 * con un override fijo del form):
 * 1) tipoPersonaRubro FISICA -> PHYSICAL / JURIDICA -> LEGAL (DECISIVO: un rubro
 *    "solo jurídica" como Incendio nunca manda PHYSICAL aunque el form sea de
 *    persona física, y viceversa).
 * 2) tipoDoc (CUIT/CUIL -> LEGAL, DNI/otros doc -> PHYSICAL) cuando el rubro no
 *    fija el tipo (AMBOS o rubro desconocido).
 * 3) Para AMBOS sin tipoDoc, el default del form (`defaultPersonTypeForBoth`).
 * Si nada resuelve -> undefined (no lo inventamos; crmbridge decide).
 */
export const mapPersonType = (input: PersonTypeInput): PersonType | undefined => {
  const rubroTp = norm(input.tipoPersonaRubro).toUpperCase();
  // El tipo del rubro single-type es decisivo y gana sobre cualquier otra señal.
  if (rubroTp === 'FISICA') return 'PHYSICAL';
  if (rubroTp === 'JURIDICA') return 'LEGAL';

  const doc = norm(input.tipoDoc).toLowerCase();
  if (doc) {
    if (doc === 'cuit' || doc === 'cuil') return 'LEGAL';
    return 'PHYSICAL';
  }

  // AMBOS (o rubro desconocido) sin tipoDoc: usa el default que decide el form.
  if (rubroTp === 'AMBOS' && input.defaultPersonTypeForBoth) {
    return input.defaultPersonTypeForBoth;
  }

  return undefined; // sin señales: crmbridge decide
};

interface AddressInput {
  street?: unknown;
  number?: unknown;
  city?: unknown;
  province?: unknown;
  zipCode?: unknown;
}

/**
 * Address §12.2 (undefined si todo vacío). zipCode = number (omitido si vacío/NaN).
 * country siempre 'Argentina' (solo AR postea al BFF).
 */
export const buildAddress = (
  input: AddressInput
): EmpresasLeadAddress | undefined => {
  const street = norm(input.street);
  const number = norm(input.number);
  const city = norm(input.city);
  const province = norm(input.province);
  const zipCode = toNumberOrUndefined(input.zipCode);

  if (!street && !number && !city && !province && zipCode === undefined) {
    return undefined;
  }

  const address: EmpresasLeadAddress = { country: 'Argentina' };
  if (street) address.street = street;
  if (number) address.number = number;
  if (city) address.city = city;
  if (province) address.province = province;
  if (zipCode !== undefined) address.zipCode = zipCode;
  return address;
};

/** Quita claves undefined de un objeto plano (no recursivo). */
const compact = <T extends object>(obj: T): T => {
  const out = {} as T;
  (Object.keys(obj) as (keyof T)[]).forEach((k) => {
    if (obj[k] !== undefined) out[k] = obj[k];
  });
  return out;
};

/**
 * Ensambla el body §12.2 a partir de partes ya mapeadas. Omite claves vacías:
 * personType/refererUrl/landingUrl undefined no viajan; contact se compacta.
 */
const buildLeadBody = (parts: {
  rubro: string;
  personType?: PersonType;
  contact: EmpresasLeadContact;
  quote: Record<string, unknown>;
  refererUrl?: unknown;
  landingUrl?: unknown;
}): EmpresasLeadBody => {
  const refererUrl = norm(parts.refererUrl) || undefined;
  const landingUrl = norm(parts.landingUrl) || undefined;
  const body: EmpresasLeadBody = {
    rubro: parts.rubro,
    contact: compact(parts.contact),
    quote: compact(parts.quote),
    source: 'Web',
  };
  if (parts.personType) body.personType = parts.personType;
  if (refererUrl) body.refererUrl = refererUrl;
  if (landingUrl) body.landingUrl = landingUrl;
  return body;
};

// ---------- Builders por form ----------

export interface ArtLeadInput {
  origen?: string;
  tipoCotizador?: string;
  landingUrl?: string;
  refererUrl?: string;
  cuit?: string;
  razonSocial?: string;
  masaSalarial?: string;
  empleados?: string;
  email?: string;
  codigoArea?: string;
  telefono?: string;
  provincia?: string;
  localidad?: string;
  nombre?: string;
  apellido?: string;
}

/** ART: personType siempre LEGAL. documentType CUIT. */
export const mapArtLead = (input: ArtLeadInput): EmpresasLeadBody => {
  const contact: EmpresasLeadContact = {
    firstName: norm(input.nombre) || undefined,
    lastName: norm(input.apellido) || undefined,
    companyName: norm(input.razonSocial) || undefined,
    email: norm(input.email) || undefined,
    phone: mapPhone(input.codigoArea, input.telefono),
    documentType: input.cuit ? 'CUIT' : undefined,
    documentNumber: norm(input.cuit) || undefined,
    address: buildAddress({ city: input.localidad, province: input.provincia }),
  };
  return buildLeadBody({
    rubro: 'ART',
    personType: 'LEGAL',
    contact,
    refererUrl: input.refererUrl,
    landingUrl: input.landingUrl,
    quote: {
      origen: norm(input.origen) || undefined,
      tipoCotizador: norm(input.tipoCotizador) || undefined,
      masaSalarial: norm(input.masaSalarial) || undefined,
      empleados: norm(input.empleados) || undefined,
    },
  });
};

export interface CaucionLeadInput {
  origen?: string;
  tipoCotizador?: string;
  landingUrl?: string;
  refererUrl?: string;
  nombre?: string;
  apellido?: string;
  codigoArea?: string;
  telefono?: string;
  email?: string;
  tipoDoc?: string;
  cuit?: string;
  razonSocial?: string;
  provincia?: string;
  localidad?: string;
  tipoCobertura?: string;
  moneda?: string;
  sumaAsegurada?: string;
}

/** Caución (rubro "ambos"): personType según tipoDoc (CUIT->LEGAL, DNI->PHYSICAL). */
export const mapCaucionLead = (input: CaucionLeadInput): EmpresasLeadBody => {
  const documentType = mapTipoDocumento(input.tipoDoc) || undefined;
  const contact: EmpresasLeadContact = {
    firstName: norm(input.nombre) || undefined,
    lastName: norm(input.apellido) || undefined,
    companyName: norm(input.razonSocial) || undefined,
    email: norm(input.email) || undefined,
    phone: mapPhone(input.codigoArea, input.telefono),
    documentType,
    documentNumber: norm(input.cuit) || undefined,
    address: buildAddress({ city: input.localidad, province: input.provincia }),
  };
  return buildLeadBody({
    rubro: 'CAUCION',
    personType: mapPersonType({ tipoDoc: input.tipoDoc }),
    contact,
    refererUrl: input.refererUrl,
    landingUrl: input.landingUrl,
    quote: {
      origen: norm(input.origen) || undefined,
      tipoCotizador: norm(input.tipoCotizador) || undefined,
      tipoCobertura: norm(input.tipoCobertura) || undefined,
      moneda: norm(input.moneda) || undefined,
      sumaAsegurada: norm(input.sumaAsegurada) || undefined,
    },
  });
};

export interface CelularLeadInput {
  origen?: string;
  landingUrl?: string;
  refererUrl?: string;
  nombre?: string;
  apellido?: string;
  codigoArea?: string;
  telefono?: string;
  email?: string;
}

/** Celular: personType siempre PHYSICAL. */
export const mapCelularLead = (input: CelularLeadInput): EmpresasLeadBody => {
  const contact: EmpresasLeadContact = {
    firstName: norm(input.nombre) || undefined,
    lastName: norm(input.apellido) || undefined,
    email: norm(input.email) || undefined,
    phone: mapPhone(input.codigoArea, input.telefono),
  };
  return buildLeadBody({
    rubro: 'CELULAR',
    personType: 'PHYSICAL',
    contact,
    refererUrl: input.refererUrl,
    landingUrl: input.landingUrl,
    quote: {
      origen: norm(input.origen) || undefined,
    },
  });
};

export interface ComercioLeadInput {
  origen?: string;
  landingUrl?: string;
  refererUrl?: string;
  nombre?: string;
  apellido?: string;
  codigoArea?: string;
  telefono?: string;
  email?: string;
  cuit?: string;
  actividad?: string;
  provincia?: string;
  localidad?: string;
}

/** Comercio: persona jurídica → personType LEGAL y documentType CUIT (como ART). */
export const mapComercioLead = (input: ComercioLeadInput): EmpresasLeadBody => {
  const contact: EmpresasLeadContact = {
    firstName: norm(input.nombre) || undefined,
    lastName: norm(input.apellido) || undefined,
    email: norm(input.email) || undefined,
    phone: mapPhone(input.codigoArea, input.telefono),
    documentType: input.cuit ? 'CUIT' : undefined,
    documentNumber: norm(input.cuit) || undefined,
    address: buildAddress({ city: input.localidad, province: input.provincia }),
  };
  return buildLeadBody({
    rubro: 'COMERCIO',
    personType: 'LEGAL',
    contact,
    refererUrl: input.refererUrl,
    landingUrl: input.landingUrl,
    quote: {
      origen: norm(input.origen) || undefined,
      actividad: norm(input.actividad) || undefined,
    },
  });
};

export interface GenericoLeadInput {
  rubro: string;
  origen?: string;
  tipoCotizador?: string;
  landingUrl?: string;
  refererUrl?: string;
  nombre?: string;
  apellido?: string;
  razonSocial?: string;
  codigoArea?: string;
  telefono?: string;
  email?: string;
  tipoDoc?: string;
  cuit?: string;
  numeroDoc?: string;
  provincia?: string;
  localidad?: string;
  codigoPostal?: string;
  fechaNacimiento?: string;
  tipoVivienda?: string;
  /** rubros.js `tipoPersona` de la landing (FISICA|JURIDICA|AMBOS). */
  tipoPersonaRubro?: TipoPersonaRubro;
  /**
   * personType por defecto SOLO para rubros AMBOS, decidido por el form.
   * FormularioIndividuo (persona física) lo pasa en 'PHYSICAL' para que un rubro
   * AMBOS (Embarcacion, AP...) lleve un personType válido. NO pisa el tipo del
   * rubro: para FISICA/JURIDICA el `tipoPersonaRubro` sigue siendo decisivo
   * (ej. Incendio = JURIDICA -> LEGAL aunque el form sea de individuo).
   */
  defaultPersonTypeForBoth?: PersonType;
  /** Campos extra de la landing -> entran a `quote`. */
  extras?: Record<string, unknown>;
}

/**
 * Genérico (landings): rubro tal cual lo manda el front. personType derivado
 * del tipoDoc y/o del tipoPersona del rubro (AMBOS sin doc -> omitido).
 * El resto de campos del form va a `quote`.
 */
export const mapGenericoLead = (input: GenericoLeadInput): EmpresasLeadBody => {
  const documentType = mapTipoDocumento(input.tipoDoc) || undefined;
  const documentNumber = norm(input.numeroDoc) || norm(input.cuit) || undefined;
  const contact: EmpresasLeadContact = {
    firstName: norm(input.nombre) || undefined,
    lastName: norm(input.apellido) || undefined,
    companyName: norm(input.razonSocial) || undefined,
    email: norm(input.email) || undefined,
    phone: mapPhone(input.codigoArea, input.telefono),
    documentType,
    documentNumber,
    address: buildAddress({
      city: input.localidad,
      province: input.provincia,
      zipCode: input.codigoPostal,
    }),
  };
  return buildLeadBody({
    rubro: input.rubro,
    personType: mapPersonType({
      tipoDoc: input.tipoDoc,
      tipoPersonaRubro: input.tipoPersonaRubro,
      defaultPersonTypeForBoth: input.defaultPersonTypeForBoth,
    }),
    contact,
    refererUrl: input.refererUrl,
    landingUrl: input.landingUrl,
    quote: {
      ...(input.extras ?? {}),
      origen: norm(input.origen) || undefined,
      tipoCotizador: norm(input.tipoCotizador) || undefined,
      fechaNacimiento: norm(input.fechaNacimiento) || undefined,
      tipoVivienda: norm(input.tipoVivienda) || undefined,
      codigoPostal: norm(input.codigoPostal) || undefined,
    },
  });
};
